/**
 * DebugPanel Component
 * Panel flotante para ver logs en dispositivos móviles
 */

import React, { useRef, useEffect, useState } from 'react';
import { formatDate } from '@/utils/dateUtils';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  BugReport as BugIcon,
  CheckCircle as CheckIcon,
  Warning as WarnIcon,
} from '@mui/icons-material';
import { useDebugLog, getDebugUIEnabled, subscribeDebugUI } from '@/hooks/useDebugLog';
import {
  getLatestInputStats,
  subscribeLatestInputStats,
  type InputStats,
} from '@/hooks/webRTCSessionStore';

export const DebugPanel: React.FC = () => {
  const [debugUIEnabled, setDebugUIEnabled] = React.useState<boolean>(() => getDebugUIEnabled());
  const { logs, isOpen, setIsOpen, filter, setFilter, clearLogs, exportLogs } = useDebugLog();
  const logsEndRef = useRef<HTMLDivElement>(null);
  // PR 5: live input traffic counters + last self-test RTT. The
  // hook pushes a snapshot to a module-level mirror every time
  // the stats change (useEffect in useWebRTC). The panel subscribes
  // to that mirror and re-renders on push. No React context needed.
  const [inputStats, setInputStats] = useState<InputStats>(() => getLatestInputStats());
  useEffect(() => {
    // Subscribe to the module-level mirror for live updates from the
    // active session's hook. The initial value comes from the lazy
    // useState init above, so we don't need to re-read on mount.
    const unsub = subscribeLatestInputStats((stats) => setInputStats(stats));
    return () => {
      unsub();
    };
  }, []);

  // Color the RTT chip: green <50ms, yellow 50-200ms, red >200ms.
  const rttColor: 'success' | 'warning' | 'error' | 'default' =
    inputStats.lastPingRttMs == null
      ? 'default'
      : inputStats.lastPingRttMs < 50
        ? 'success'
        : inputStats.lastPingRttMs < 200
          ? 'warning'
          : 'error';

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (isOpen && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen]);

  // Subscribe to debug UI flag changes so the panel/button updates live
  useEffect(() => {
    const unsub = subscribeDebugUI((v) => setDebugUIEnabled(v));
    return () => {
      unsub();
    };
  }, []);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'error';
      case 'warn':
        return 'warning';
      case 'info':
        return 'info';
      case 'debug':
        return 'default';
      default:
        return 'default';
    }
  };

  if (!debugUIEnabled) return null;

  if (!isOpen) {
    return (
      <IconButton
        onClick={() => setIsOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          boxShadow: 3,
        }}
      >
        <BugIcon />
      </IconButton>
    );
  }

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        maxHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 6,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'background.paper',
          flexWrap: 'wrap',
        }}
      >
        <BugIcon fontSize="small" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Debug Logs ({logs.length})
        </Typography>

        {/* PR 5: input traffic + self-test RTT. Sits in the header so
            operators see degraded states without expanding the log
            list. Sent/dropped are cumulative per session; backoff is
            a leading indicator of saturation. RTT comes from the
            one-shot self-test on data channel open. */}
        <Tooltip
          title={`Input events sent=${inputStats.sent}, dropped=${inputStats.dropped}, backoff waits=${inputStats.backoffWaits}. A non-zero 'dropped' count means clicks are not reaching the host.`}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" data-testid="debug-input-stats">
            <Chip
              size="small"
              label={`sent ${inputStats.sent}`}
              color={inputStats.dropped > 0 ? 'warning' : 'default'}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`drop ${inputStats.dropped}`}
              color={inputStats.dropped > 0 ? 'error' : 'default'}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`backoff ${inputStats.backoffWaits}`}
              color={inputStats.backoffWaits > inputStats.sent / 2 ? 'warning' : 'default'}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={
                rttColor === 'success' ? (
                  <CheckIcon />
                ) : rttColor === 'error' || rttColor === 'warning' ? (
                  <WarnIcon />
                ) : undefined
              }
              label={
                inputStats.lastPingRttMs == null ? 'RTT —' : `RTT ${inputStats.lastPingRttMs}ms`
              }
              color={rttColor}
              variant="outlined"
            />
          </Stack>
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Filtrar</InputLabel>
          <Select value={filter} label="Filtrar" onChange={(e) => setFilter(e.target.value as any)}>
            'all' | 'webrtc' | 'proxy' | 'error'
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="webrtc">WebRTC</MenuItem>
            <MenuItem value="proxy">Proxy</MenuItem>
            <MenuItem value="error">Errores</MenuItem>
          </Select>
        </FormControl>

        <IconButton size="small" onClick={exportLogs} title="Descargar logs">
          <DownloadIcon fontSize="small" />
        </IconButton>

        <IconButton size="small" onClick={clearLogs} title="Limpiar logs">
          <DeleteIcon fontSize="small" />
        </IconButton>

        <IconButton size="small" onClick={() => setIsOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Logs Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1,
          backgroundColor: '#1e1e1e',
          color: '#d4d4d4',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
        }}
      >
        {logs.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', p: 2 }}>
            No hay logs aún
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {logs.map((log) => {
              const time = log.timestamp ? formatDate(log.timestamp, 'LTS') : '';
              return (
                <Box key={log.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Typography
                    component="span"
                    sx={{
                      color: '#858585',
                      fontSize: '0.7rem',
                      flexShrink: 0,
                      minWidth: 70,
                    }}
                  >
                    {time}
                  </Typography>
                  <Chip
                    label={log.level.toUpperCase()}
                    size="small"
                    color={getLevelColor(log.level) as any}
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      flex: 1,
                      wordBreak: 'break-word',
                      color:
                        log.level === 'error'
                          ? '#f48771'
                          : log.level === 'warn'
                            ? '#dcdcaa'
                            : '#d4d4d4',
                    }}
                  >
                    {log.message}
                  </Typography>
                </Box>
              );
            })}
            <div ref={logsEndRef} />
          </Stack>
        )}
      </Box>
    </Paper>
  );
};
